import React, { useEffect, useState } from "react";
import { Box, Container, Typography, Grid, TextField, Button, Card, CardContent, Avatar, useTheme, Link } from '@mui/material';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { DataGrid } from '@mui/x-data-grid';

import config from "../config/config";
import RegisteredUserContactForm from "../components/forms/RegisteredUserContactForm";
import { useGetRegisteredUserContactSubmissions } from "../hooks/appHooks";

const Support = () => {
    const [rows, setRows] = useState([]);
    const { isLoading: isRegisteredUserContactSubmissionLoading, error: registeredUserContactSubmissionError, data: registeredUserContactSubmissionData } = useGetRegisteredUserContactSubmissions();

    useEffect(() => {
        if (registeredUserContactSubmissionData) {
            const rowsData = registeredUserContactSubmissionData.map((item, index) => ({
                id: index + 1, // Generate unique id for each row
                ...item, // Spread the rest of the data as row values
            }));

            setRows(rowsData);
        }
    }, [registeredUserContactSubmissionData]);

    const columns = [
        { field: 'id', headerName: '#', width: 30, },
        { field: 'request_id', headerName: 'Request ID', width: 250, headerClassName: 'bold-header' },
        { field: 'created_at', headerName: 'Request Date', width: 150, },
        { field: 'subject', headerName: 'Subject', width: 200, },
        { field: 'message', headerName: 'Message', width: 350, },
        { field: 'priority', headerName: 'Priority', width: 150, },
        { field: 'status', headerName: 'Status', width: 150, },
    ];

    if (isRegisteredUserContactSubmissionLoading) {
        return <div>Loading...</div>;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                marginRight: -1,
            }}
        >
            <Scrollbars>
                <Box
                    sx={{
                        marginRight: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        // border: 2,
                    }}
                >
                    <Grid container spacing={4} sx={{ marginTop: 1 }}>
                        {/* Conditionally Render Header for Existing Request */}
                        {rows && rows.length > 0 && (
                            <>
                                {/* Header for Existing Request */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="h4"
                                        align="center"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'primary.main',
                                            mb: 4,
                                            textTransform: 'uppercase', // Capitalize the header for emphasis
                                            letterSpacing: 1, // Slight spacing for a cleaner look
                                        }}
                                    >
                                        Existing Request Data
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        align="center"
                                        sx={{
                                            fontStyle: 'italic', // To differentiate it as an explanatory note
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Below is the data for your current active request. Please review it and let us know if you need any changes or further assistance.
                                    </Typography>
                                </Grid>

                                {/* DataGrid Displaying Existing Request Data */}
                                <Grid item xs={12}>
                                    <DataGrid
                                        rows={rows}
                                        columns={columns}
                                        pageSize={5}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            boxShadow: 3,
                                            backgroundColor: 'background.paper',
                                            padding: 2,
                                            '& .MuiDataGrid-columnHeader': {
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Rest of the Grid for Support and Contact Information */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h3"
                                align="center"
                                sx={{
                                    fontWeight: 'bold',
                                    mb: 4, // Slightly more bottom margin for spacing
                                    color: 'primary.main', // Color adjustment using the theme's primary color
                                }}
                            >
                                Need Help?
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                If you have any questions or need assistance with our services, please don't hesitate to contact us. We're here to help!
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Our support team is available 24/7 and is committed to providing prompt and accurate assistance to our valued users.
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                You can reach us through the following channels:
                            </Typography>
                            <ul style={{ paddingLeft: '20px' }}>
                                <li>
                                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                                        Email: <Link href={`mailto:${config.supportMail}`} color="primary.dark">{config.supportMail}</Link>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                                        Phone: <Link href={`tel:${config.phoneNumber}`} color="primary.dark">{config.phoneNumber}</Link>
                                    </Typography>
                                </li>
                            </ul>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ boxShadow: 3 }}>
                                <CardContent>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 2,
                                            color: 'secondary.main', // Adjusted color
                                        }}
                                    >
                                        Contact Support
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 3 }}>
                                        Use this form to contact us about any issues or requests. We'll respond as soon as possible.
                                    </Typography>
                                    <RegisteredUserContactForm />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>


                </Box>
            </Scrollbars>
        </Box>
    );
};

export default Support;